import {
    CanvasTexture,
    LinearFilter,
    LinearMipmapLinearFilter,
    NoColorSpace,
    RepeatWrapping,
} from "three";

// Silver vinyl the artwork is printed on; it shows through wherever the
// ink is thin and carries the rainbow on its own in the die-cut border.
const FOIL = [214, 216, 220];

/** Fraction of the texture kept empty around the disc so the cut edge can fade. */
export const STICKER_PAD = 0.04;

let envTexture = null;

/** Procedural studio environment as an equirect texture (softboxes + sky). */
export function studioEnvTexture() {
    if (envTexture) return envTexture;

    const w = 1024;
    const h = 512;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");

    // vertical gradient: bright ceiling, mid walls, darker floor
    const gradient = ctx.createLinearGradient(0, 0, 0, h);
    gradient.addColorStop(0, "#f4f5f7");
    gradient.addColorStop(0.45, "#c9ccd2");
    gradient.addColorStop(0.55, "#aeb2b9");
    gradient.addColorStop(1, "#7c8087");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);

    const blob = (x, y, rx, ry, alpha) => {
        const radial = ctx.createRadialGradient(x, y, 0, x, y, Math.max(rx, ry));
        radial.addColorStop(0, `rgba(255,255,255,${alpha})`);
        radial.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = radial;
        ctx.save();
        ctx.translate(x, y);
        ctx.scale(1, ry / rx);
        ctx.translate(-x, -y);
        ctx.beginPath();
        ctx.arc(x, y, rx, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    };
    blob(w * 0.25, h * 0.22, 190, 110, 0.98);
    blob(w * 0.72, h * 0.3, 130, 80, 0.85);
    blob(w * 0.5, h * 0.75, 220, 60, 0.25);

    envTexture = new CanvasTexture(canvas);
    envTexture.generateMipmaps = true;
    envTexture.minFilter = LinearMipmapLinearFilter;
    envTexture.magFilter = LinearFilter;
    envTexture.wrapS = RepeatWrapping;
    envTexture.colorSpace = NoColorSpace;
    return envTexture;
}

/**
 * Rasterise an SVG centred in a square canvas. The file carries only a
 * viewBox, so a size has to be forced on it before the browser will draw
 * it at anything better than its default object size.
 */
async function rasterizeSvg(url, size, pad) {
    const source = await fetch(url).then((response) => response.text());
    const svg = new DOMParser().parseFromString(
        source,
        "image/svg+xml",
    ).documentElement;
    const inner = Math.round(size * (1 - 2 * pad));
    svg.setAttribute("width", inner);
    svg.setAttribute("height", inner);

    const blobUrl = URL.createObjectURL(
        new Blob([new XMLSerializer().serializeToString(svg)], {
            type: "image/svg+xml",
        }),
    );
    const image = new Image();
    image.src = blobUrl;
    try {
        await image.decode();
    } finally {
        URL.revokeObjectURL(blobUrl);
    }

    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const offset = Math.round(size * pad);
    ctx.drawImage(image, offset, offset, inner, inner);
    return ctx.getImageData(0, 0, size, size);
}

function canvasTextureFrom(pixels, size, mipmapped) {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    canvas.getContext("2d").putImageData(new ImageData(pixels, size, size), 0, 0);

    const texture = new CanvasTexture(canvas);
    texture.colorSpace = NoColorSpace;
    texture.anisotropy = 8;
    texture.premultiplyAlpha = false;
    if (mipmapped) {
        texture.generateMipmaps = true;
        texture.minFilter = LinearMipmapLinearFilter;
    }
    return texture;
}

/**
 * Build the two maps the foil shader reads: the albedo, whose alpha is the
 * die-cut disc, and the vinyl height field that bevels the cut edge.
 * The die cut is a circle, so its distance field is exact rather than the
 * euclidean transform holosticker needs for arbitrary artwork.
 */
export async function buildStickerMaps(url, { size = 1024, ink = 1 } = {}) {
    const art = await rasterizeSvg(url, size, STICKER_PAD);
    const radius = size * (0.5 - STICKER_PAD);
    const centre = size * 0.5;
    // narrow rounded shoulder just inside the cut edge
    const shoulder = Math.max(4, size * 0.009);

    const albedo = new Uint8ClampedArray(size * size * 4);
    const height = new Uint8ClampedArray(size * size * 4);
    const inkGamma = 1 / Math.max(ink, 1);
    const inkScale = Math.min(1, ink);

    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            const i = (y * size + x) * 4;
            const inward =
                radius - Math.hypot(x + 0.5 - centre, y + 0.5 - centre);

            const alpha = art.data[i + 3] / 255;
            // ink control: < 1 fades the print into the foil
            const a = inkScale * alpha ** inkGamma;
            albedo[i] = FOIL[0] * (1 - a) + art.data[i] * a;
            albedo[i + 1] = FOIL[1] * (1 - a) + art.data[i + 1] * a;
            albedo[i + 2] = FOIL[2] * (1 - a) + art.data[i + 2] * a;
            // antialiased die-cut edge
            albedo[i + 3] = (inward + 0.5) * 255;

            // 1 across the sticker body, rolling to 0 at the cut line
            const t = Math.min(Math.max(inward / shoulder, 0), 1);
            height[i] = 255 * t * t * (3 - 2 * t);
            height[i + 3] = 255;
        }
    }

    return {
        map: canvasTextureFrom(albedo, size, false),
        height: canvasTextureFrom(height, size, true),
    };
}
